import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Route, User } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  fetchWorkflowExecutionLogs,
  formatWorkflowDate,
  STATUS_PILL_CLASSES,
} from '../../utils/workflowInsightsApi';
import WorkflowExecutionDetailsModal from './WorkflowExecutionDetailsModal';

const ACTION_OPTIONS = [
  { value: 'all', label: 'All actions' },
  { value: 'send_email', label: 'Email' },
  { value: 'send_sms', label: 'SMS' },
  { value: 'wait', label: 'Wait' },
  { value: 'end_of_workflow', label: 'End of workflow' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'executed', label: 'Executed' },
  { value: 'finished', label: 'Finished' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'waiting', label: 'Waiting' },
];

const WorkflowExecutionLogsTab = ({
  token,
  workflowId,
  agencyId,
  onViewExecutionPath,
}) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [action, setAction] = useState('all');
  const [status, setStatus] = useState('all');
  const [contactId, setContactId] = useState('');
  const [selectedLogId, setSelectedLogId] = useState(null);

  const load = useCallback(async () => {
    if (!token || !workflowId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchWorkflowExecutionLogs({
        token,
        workflowId,
        agencyId,
        params: {
          page,
          page_size: pageSize,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          action,
          status,
          contact_id: contactId || undefined,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError('Failed to load execution logs.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [token, workflowId, agencyId, page, pageSize, startDate, endDate, action, status, contactId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="workflow-execution-logs-tab">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Execution logs</h2>
        <p className="mt-1 text-sm text-slate-500">
          View a history and details of all executions performed by this workflow
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Start date</label>
          <Input type="date" value={startDate} onChange={(e) => { setPage(1); setStartDate(e.target.value); }} />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">End date</label>
          <Input type="date" value={endDate} onChange={(e) => { setPage(1); setEndDate(e.target.value); }} />
        </div>
        <div className="space-y-1 min-w-[140px]">
          <label className="text-xs text-slate-500">Action</label>
          <Select value={action} onValueChange={(value) => { setPage(1); setAction(value); }}>
            <SelectTrigger data-testid="execution-log-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 min-w-[140px]">
          <label className="text-xs text-slate-500">Status</label>
          <Select value={status} onValueChange={(value) => { setPage(1); setStatus(value); }}>
            <SelectTrigger data-testid="execution-log-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 min-w-[180px] flex-1">
          <label className="text-xs text-slate-500">Contact</label>
          <Input
            placeholder="Select contact"
            value={contactId}
            onChange={(e) => { setPage(1); setContactId(e.target.value); }}
          />
        </div>
        <Button variant="outline" size="icon" onClick={load} data-testid="execution-log-refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <Button variant="link" className="ml-2 h-auto p-0" onClick={load}>Retry</Button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-medium text-slate-500">
            <tr>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Executed on (EDT -04:00)</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Loading execution logs...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <p className="font-medium text-slate-700">No execution logs yet</p>
                  <p className="mt-1 text-sm text-slate-500">Logs will appear once this workflow starts running.</p>
                </td>
              </tr>
            )}
            {!loading && items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100" data-testid={`execution-log-row-${row.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
                      {row.initials}
                    </span>
                    <span className="font-medium text-slate-800">{row.contact_name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{row.action?.label || '—'}</td>
                <td className="px-4 py-3">
                  <span className={cn(
                    'inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
                    STATUS_PILL_CLASSES[row.status] || STATUS_PILL_CLASSES.executed,
                  )}>
                    {row.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{formatWorkflowDate(row.executed_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="text-sm font-medium text-blue-600 hover:text-blue-700"
                      onClick={() => setSelectedLogId(row.id)}
                      data-testid={`execution-log-view-${row.id}`}
                    >
                      View details
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="See Contact Execution Path"
                      onClick={() => onViewExecutionPath?.(row)}
                    >
                      <Route size={15} />
                    </Button>
                    <Button variant="ghost" size="icon" title="View contact">
                      <User size={15} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <span>Showing page {page}</span>
          <div className="flex items-center gap-2">
            <Select value={String(pageSize)} onValueChange={(value) => { setPage(1); setPageSize(Number(value)); }}>
              <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 25, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      <WorkflowExecutionDetailsModal
        open={Boolean(selectedLogId)}
        onClose={() => setSelectedLogId(null)}
        token={token}
        workflowId={workflowId}
        agencyId={agencyId}
        logId={selectedLogId}
      />
    </div>
  );
};

export default WorkflowExecutionLogsTab;
